import {
	dispatchAcknowledgedEvent,
	openResourceSection,
} from '../../core/resourceEvents';
import {highlightExistingAction} from '../../core/actionHighlighter';
import {
	SYSTEM_BACKUP_FILTER_EVENT,
	SYSTEM_BACKUP_FOCUS_EVENT,
	SYSTEM_BACKUP_SECTION_EVENT,
} from './events';

const SYSTEM_BACKUP_SECTIONS = new Set(['maintenance.system_backup']);

/**
 * Open the System Backup accordion and carry out a highlighted toolbar
 * action.
 *
 * `refresh` and `bulk_select_delete` never resolve an entity - the backend
 * workflow templates for both are section-only (`opens_section`, no
 * resolver) - so both are worked here exactly as alarms works them, by
 * finding and highlighting (or clicking, when `action_activation` is
 * `auto`) the existing Refresh / Delete control on the table.
 *
 * @param {object} command experience command
 * @return {Promise<void>} completion
 */
export async function openSystemBackupSection(command = {}) {
	const seen = await openResourceSection(
		SYSTEM_BACKUP_SECTION_EVENT,
		'maintenance.system_backup',
		SYSTEM_BACKUP_SECTIONS,
	);
	const target = command.params?.action_target;
	const activation = {
		activate: command.params?.action_activation === 'auto',
		commandId: command.command_id,
	};
	if (target === 'refresh' &&
		!highlightExistingAction(['Refresh'], [], activation)) {
		throw new Error('system_backup_refresh_unavailable');
	}
	if (target === 'bulk_select_delete' &&
		!highlightExistingAction(['Delete'], [], activation)) {
		throw new Error('system_backup_bulk_selection_unavailable');
	}
	// Carried back rather than awaited and dropped. The section says
	// what the page actually opened; a wrapper returning nothing
	// turns that into an empty acknowledgement, and the backend then
	// cannot tell a section that opened from one that did not.
	return seen;
}

/**
 * Apply a System Backup list query or column filter.
 * @param {object} command filter command
 * @return {Promise<void>} completion acknowledgement
 */
export function applySystemBackupFilter(command) {
	const params = command.params || {};
	const query = String(params.query || '').trim();
	const filters = {};
	Object.entries(params).forEach(([key, value]) => {
		if (key === 'query' || key === 'resource') return;
		const text = String(value ?? '').trim();
		if (text) filters[key] = text;
	});
	if (!query && !Object.keys(filters).length) {
		return Promise.reject(new Error('invalid_system_backup_filter'));
	}
	return dispatchAcknowledgedEvent(SYSTEM_BACKUP_FILTER_EVENT, {
		query,
		...filters,
		resource: 'system_backup',
	});
}

/**
 * Focus a resolved System Backup row, and download it when asked.
 *
 * The `download` workflow template resolves an entity and opens the
 * section same as `search`, then carries `highlight_action: "download"` -
 * so a resolved download is worked here rather than through a modal: there
 * is nothing to confirm. The table itself refuses the download when the
 * row is not yet ready (`is_file_created !== 1`).
 *
 * @param {object} command focus command
 * @return {Promise<void>} completion acknowledgement
 */
export function focusSystemBackup(command) {
	const entityId = command.params?.entity_id;
	if (typeof entityId !== 'string' || !entityId) {
		return Promise.reject(new Error('invalid_system_backup_entity'));
	}
	return dispatchAcknowledgedEvent(SYSTEM_BACKUP_FOCUS_EVENT, {
		entityId,
		action_target: command.params?.action_target,
		action_activation: command.params?.action_activation,
		resource: 'system_backup',
	});
}
